// components
import { Calculate } from "@mui/icons-material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CameraIcon from "@mui/icons-material/Camera";
import EmojiFoodBeverageIcon from "@mui/icons-material/EmojiFoodBeverage";
import GroupsIcon from "@mui/icons-material/Groups";
import InventoryIcon from "@mui/icons-material/Inventory";
import LinkIcon from "@mui/icons-material/Link";
import PaymentIcon from "@mui/icons-material/Payment";
import RequestQuote from "@mui/icons-material/RequestQuote";
import SettingsIcon from "@mui/icons-material/Settings";
import TranslateIcon from "@mui/icons-material/Translate";
import { useTranslation } from "react-i18next";
import { TbCalendarUser } from "react-icons/tb";

import SvgIconStyle from "@/components/SvgIconStyle";
import { PAGE_PERMISSIONS } from "@/constants/permissions";

// ----------------------------------------------------------------------

const getIcon = (name: string) => <SvgIconStyle src={`/icons/${name}.svg`} sx={{ width: 1, height: 1 }} />;

interface Icons {
    [key: string]: React.ReactNode;
}

const ICONS: Icons = {
    user: getIcon("ic_user"),
    translate: <TranslateIcon />,
    ecommerce: getIcon("ic_ecommerce"),
    analytics: getIcon("ic_analytics"),
    dashboard: getIcon("ic_dashboard"),
    feedbacks: <AssignmentIcon />,
    calculator: <Calculate />,
    invoice: getIcon("ic_invoice"),
    payment: <PaymentIcon />,
    groups: <GroupsIcon />,
    foodIcon: <EmojiFoodBeverageIcon />,
    requestQuoteIcon: <RequestQuote />,
    inventoryIcon: <InventoryIcon />,
    simulations: <CameraIcon />,
    settings: <SettingsIcon />,
    calendar: <TbCalendarUser />,
    link: <LinkIcon />,
};

interface NavItem {
    title: string;
    path: string;
    icon: React.ReactNode;
    info?: React.ReactNode;
    children?: NavItem[];
    permissions?: string[];
    type?: "select";
    options?: Array<{ value: string; label: string }>;
}

interface NavSection {
    subheader: string;
    items: NavItem[];
}

const SidebarConfig = (): NavSection[] => {
    const { t } = useTranslation();

    return [
        {
            subheader: t("general"),
            items: [
                {
                    title: t("offers"),
                    path: "/dashboard/offers",
                    icon: ICONS.requestQuoteIcon,
                    permissions: PAGE_PERMISSIONS.OFFERS,
                },
                {
                    title: t("templates"),
                    path: "/dashboard/templates",
                    icon: ICONS.inventoryIcon,
                    permissions: PAGE_PERMISSIONS.TEMPLATES,
                },
                {
                    title: t("sections"),
                    path: "/dashboard/sections",
                    icon: ICONS.analytics,
                    permissions: PAGE_PERMISSIONS.SECTIONS,
                },
                {
                    title: t("calc_panel"),
                    path: "/dashboard/calc",
                    icon: ICONS.calculator,
                    permissions: PAGE_PERMISSIONS.CALCULATORS,
                },
                {
                    title: "Shortlinks",
                    path: "/dashboard/shortlinks",
                    icon: ICONS.link,
                    permissions: PAGE_PERMISSIONS.SHORTLINKS,
                },
            ],
        },
        {
            subheader: t("elit_phone"),
            items: [
                {
                    title: t("agent_calendars"),
                    path: "/dashboard/agent-calendars",
                    icon: ICONS.calendar,
                    permissions: PAGE_PERMISSIONS.AGENT_CALENDARS,
                },
            ],
        },
        {
            subheader: t("mobile_app"),
            items: [
                {
                    title: t("app_users"),
                    path: "/dashboard/patients",
                    icon: ICONS.groups,
                    permissions: PAGE_PERMISSIONS.USERS,
                },
                {
                    title: t("strings"),
                    path: "/dashboard/strings",
                    icon: ICONS.translate,
                    permissions: PAGE_PERMISSIONS.STRINGS,
                },
                {
                    title: t("extra_treatments"),
                    path: "/dashboard/extra-treatments",
                    icon: ICONS.foodIcon,
                    permissions: PAGE_PERMISSIONS.EXTRA_TREATMENTS,
                },
                {
                    title: t("promotions"),
                    path: "/dashboard/promotions",
                    icon: ICONS.ecommerce,
                    permissions: PAGE_PERMISSIONS.PROMOTIONS,
                },
                {
                    title: t("feedbacks"),
                    path: "/dashboard/feedbacks",
                    icon: ICONS.feedbacks,
                    permissions: PAGE_PERMISSIONS.FEEDBACKS,
                },
                {
                    title: t("tutorials"),
                    path: "/dashboard/tutorials",
                    icon: ICONS.invoice,
                    permissions: PAGE_PERMISSIONS.TUTORIALS,
                },
            ],
        },
        {
            subheader: t("elit_ai"),
            items: [
                {
                    title: t("simulations"),
                    path: "/dashboard/elitai/simulations",
                    icon: ICONS.simulations,
                    permissions: PAGE_PERMISSIONS.ELITAI_SIMULATIONS,
                },
                {
                    title: t("prompts"),
                    path: "/dashboard/prompts",
                    icon: ICONS.invoice,
                    permissions: PAGE_PERMISSIONS.ELITAI_SIMULATIONS,
                },
            ],
        },
        {
            subheader: t("twilio"),
            items: [
                {
                    title: t("machine_caller"),
                    path: "/dashboard/machine-caller",
                    icon: ICONS.invoice,
                    permissions: PAGE_PERMISSIONS.MACHINE_CALLER,
                },
            ],
        },
        {
            subheader: t("settings"),
            items: [
                {
                    title: t("users"),
                    path: "/dashboard/users",
                    icon: ICONS.user,
                    permissions: PAGE_PERMISSIONS.USERS,
                },
                {
                    title: t("permissions"),
                    path: "/dashboard/permissions",
                    icon: ICONS.settings,
                    permissions: PAGE_PERMISSIONS.PERMISSIONS,
                },
            ],
        },
        {
            subheader: t("preferences"),
            items: [
                {
                    title: t("language"),
                    path: "language-select",
                    icon: ICONS.translate,
                    type: "select",
                    options: [
                        { value: "en", label: "English" },
                        { value: "de", label: "Deutsch" },
                        { value: "tr", label: "Türkçe" },
                    ],
                },
            ],
        },
    ];
};

export default SidebarConfig;
